import ApiService from "@/core/services/ApiService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";

// 赏品接口
export interface MatchPoolItem {
  id?: number;
  match_pool_id?: number;
  product_id: number;
  product?: {
    id: number;
    name: string;
    image: string | null;
    recycle_price: number;
  };
  quantity: number;
  sort_order: number;
}

// 奖励配置接口
export interface MatchPoolReward {
  id?: number;
  match_pool_id?: number;
  match_count: number;
  product_id: number;
  product?: {
    id: number;
    name: string;
    image: string | null;
    recycle_price: number;
  };
  cost: number;
}

// 池子接口
export interface MatchPool {
  id: number;
  name: string;
  price: number;
  item_count: number;
  default_quantity: number;
  field_size: number;
  status: string;
  status_display?: string;
  box_type: string;
  box_type_display?: string;
  image: string | null;
  description: string | null;
  match_pool_items?: MatchPoolItem[];
  match_pool_rewards?: MatchPoolReward[];
  created_by?: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
  // 统计字段
  total_games?: number;
  total_revenue?: number;
  total_cost?: number;
  profit?: number;
  profit_rate?: number;
}

// 下拉选项接口
export interface MatchPoolOption {
  id: number;
  name: string;
}

// 统计数据接口
export interface MatchPoolStats {
  total_games: number;
  total_revenue: number;
  total_cost: number;
  actual_profit: number;
  actual_profit_rate: number;
  match_count_distribution: Array<{
    match_count: number;
    count: number;
    percentage: number;
  }>;
  user_gain_distribution?: {
    winners_count: number;
    winners_rate: number;
    losers_count: number;
    losers_rate: number;
  };
}

// 模拟结果接口
export interface SimulationResult {
  simulation_count: number;
  average_matches: number;
  average_cost: number;
  average_reward_value: number;
  expected_profit: number;
  expected_profit_rate: number;
  match_distribution: Array<{
    match_count: number;
    count: number;
    percentage: number;
    reward_name?: string;
  }>;
}

@Module
export default class MatchPoolModule extends VuexModule {
  match_pools_list = [] as Array<MatchPool>;
  match_pools_count = 0;
  match_pool = {} as MatchPool;
  all_match_pools = [] as Array<MatchPoolOption>;
  match_pool_stats = {} as MatchPoolStats;

  get currentMatchPoolsList(): Array<MatchPool> {
    return this.match_pools_list;
  }

  get currentMatchPool(): MatchPool {
    return this.match_pool;
  }

  get currentMatchPoolsCount(): number {
    return this.match_pools_count;
  }

  get currentAllMatchPools(): Array<MatchPoolOption> {
    return this.all_match_pools;
  }

  get currentMatchPoolStats(): MatchPoolStats {
    return this.match_pool_stats;
  }

  @Mutation
  [Mutations.SET_MATCH_POOLS_LIST](match_pools_list: Array<MatchPool>) {
    this.match_pools_list = match_pools_list;
  }

  @Mutation
  [Mutations.SET_MATCH_POOL](match_pool: MatchPool) {
    this.match_pool = match_pool;
  }

  @Mutation
  [Mutations.SET_MATCH_POOLS_COUNT](count: number) {
    this.match_pools_count = count;
  }

  @Mutation
  [Mutations.SET_ALL_MATCH_POOLS](all_match_pools: Array<MatchPoolOption>) {
    this.all_match_pools = all_match_pools;
  }

  @Mutation
  [Mutations.SET_MATCH_POOL_STATS](stats: MatchPoolStats) {
    this.match_pool_stats = stats;
  }

  @Action
  [Actions.GET_MATCH_POOLS](params: Record<string, unknown>) {
    ApiService.setHeader();

    let queryString = "?";
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryString += "&" + key + "=" + params[key];
        }
      });
    }

    return new Promise<void>((resolve, reject) => {
      ApiService.get("admin/match_pools", queryString)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_MATCH_POOLS_LIST, data["match_pools"] || []);
          this.context.commit(Mutations.SET_MATCH_POOLS_COUNT, data["total_result"] || 0);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.GET_MATCH_POOL](id: number) {
    ApiService.setHeader();

    return new Promise<void>((resolve, reject) => {
      ApiService.get(`admin/match_pools/${id}`)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_MATCH_POOL, data["match_pool"]);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.CREATE_MATCH_POOL](params: Record<string, unknown>) {
    ApiService.setHeader();

    return new Promise<{ success: boolean; error_msg?: string; match_pool?: MatchPool }>((resolve, reject) => {
      ApiService.post("admin/match_pools", { match_pool: params })
        .then(({ data }) => {
          if (data.success) {
            this.context.commit(Mutations.SET_MATCH_POOL, data["match_pool"]);
          }
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject(response?.data);
        });
    });
  }

  @Action
  [Actions.UPDATE_MATCH_POOL](credentials: { id: number; values: Record<string, unknown> }) {
    ApiService.setHeader();

    return new Promise<{ success: boolean; error_msg?: string }>((resolve, reject) => {
      ApiService.put(`admin/match_pools/${credentials.id}`, { match_pool: credentials.values })
        .then(({ data }) => {
          if (data.success) {
            this.context.commit(Mutations.SET_MATCH_POOL, data["match_pool"]);
          }
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject(response?.data);
        });
    });
  }

  @Action
  [Actions.DELETE_MATCH_POOL](id: number) {
    ApiService.setHeader();

    return new Promise<{ success: boolean; error_msg?: string }>((resolve, reject) => {
      ApiService.delete(`admin/match_pools/${id}`)
        .then(({ data }) => {
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject(response?.data);
        });
    });
  }

  @Action
  [Actions.GET_ALL_MATCH_POOLS]() {
    ApiService.setHeader();

    return new Promise<void>((resolve, reject) => {
      ApiService.get("admin/match_pools/all")
        .then(({ data }) => {
          this.context.commit(Mutations.SET_ALL_MATCH_POOLS, data["match_pools"] || []);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.GET_MATCH_POOL_STATS](id: number) {
    ApiService.setHeader();

    return new Promise<void>((resolve, reject) => {
      ApiService.get(`admin/match_pools/${id}/statistics`)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_MATCH_POOL_STATS, data["statistics"] || {});
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.SIMULATE_MATCH_POOL](params: { id: number; times?: number }) {
    ApiService.setHeader();

    return new Promise<SimulationResult>((resolve, reject) => {
      ApiService.post(`admin/match_pools/${params.id}/simulate`, {
        times: params.times || 10000
      })
        .then(({ data }) => {
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject(response?.data);
        });
    });
  }
}
